<!-- SearchCouponReservationInput.vue -->
<script setup>
import { reactive, watch, onUnmounted } from "vue";
import { useCouponReservationStore } from "@/stores/useCouponReservationStore";
import debounce from "lodash.debounce";

// ✅ Prop que controla visibilidade do campo ID Usuário
// false → usuário comum (só vê as próprias reservas; campo oculto e imutável)
// true  → Sys (vê tudo; campo visível e funcional)
defineProps({
  isSys: {
    type: Boolean,
    default: false,
  },
});

const store = useCouponReservationStore();

const local = reactive({
  couponId:          store.filters.couponId,
  userId:            store.filters.userId,
  subscriptionId:    store.filters.subscriptionId,
  couponCode:        store.filters.couponCode,
  userName:          store.filters.userName,
  reservationStatus: store.filters.reservationStatus,
});

const debouncedNumeric = debounce((field, value) => {
  const trimmed = String(value ?? "").trim();
  if (trimmed !== "" && (!/^\d+$/.test(trimmed) || Number(trimmed) <= 0)) return;
  store.setFilter(field, trimmed);
}, 400);

const debouncedText = debounce((field, value) => {
  store.setFilter(field, String(value ?? "").trim());
}, 300);

watch(() => local.couponId,          (v) => debouncedNumeric("couponId", v));
watch(() => local.userId,            (v) => debouncedNumeric("userId", v));
watch(() => local.subscriptionId,    (v) => debouncedNumeric("subscriptionId", v));
watch(() => local.couponCode,        (v) => debouncedText("couponCode", v));
watch(() => local.userName,          (v) => debouncedText("userName", v));
watch(() => local.reservationStatus, (v) => store.setFilter("reservationStatus", v));

function clearAll() {
  local.couponId          = "";
  local.userId            = "";
  local.subscriptionId    = "";
  local.couponCode        = "";
  local.userName          = "";
  local.reservationStatus = "";
  store.clearFilters();
}

const hasActiveFilters = () =>
  Object.values(local).some((v) => String(v ?? "").trim() !== "");

onUnmounted(() => {
  store.clearFilters();
});
</script>

<template>
  <div class="filter-panel">
    <div class="filter-row">
      <!-- ID Cupom -->
      <div class="filter-field">
        <label class="filter-label">ID Cupom</label>
        <input
          v-model="local.couponId"
          type="text"
          inputmode="numeric"
          pattern="[0-9]*"
          placeholder="ex: 3"
          class="filter-input"
        />
      </div>

      <!-- ID Usuário: visível apenas para Sys -->
      <div v-if="isSys" class="filter-field">
        <label class="filter-label">ID Usuário</label>
        <input
          v-model="local.userId"
          type="text"
          inputmode="numeric"
          pattern="[0-9]*"
          placeholder="ex: 7"
          class="filter-input"
        />
      </div>

      <!-- ID Assinatura (nullable no banco) -->
      <div class="filter-field">
        <label class="filter-label">ID Assin.</label>
        <input
          v-model="local.subscriptionId"
          type="text"
          inputmode="numeric"
          pattern="[0-9]*"
          placeholder="ex: 42"
          class="filter-input"
        />
      </div>

      <!-- Código do cupom -->
      <div class="filter-field">
        <label class="filter-label">CUPOM</label>
        <input
          v-model="local.couponCode"
          type="text"
          placeholder="ex: PROMO2026"
          class="filter-input"
        />
      </div>

      <!-- Nome do usuário -->
      <div class="filter-field">
        <label class="filter-label">Usuário</label>
        <input
          v-model="local.userName"
          type="text"
          placeholder="nome de usuário…"
          class="filter-input"
        />
      </div>

      <!-- Status da reserva -->
      <!-- ✅ Computado no backend pela regra consumed > cancelled > expired > active -->
      <div class="filter-field">
        <label class="filter-label">Status</label>
        <select
          v-model="local.reservationStatus"
          class="filter-input filter-select"
        >
          <option value="">Todos</option>
          <option value="active">Ativa</option>
          <option value="consumed">Consumida</option>
          <option value="cancelled">Cancelada</option>
          <option value="expired">Expirada</option>
        </select>
      </div>

      <!-- Botão limpar -->
      <div v-if="hasActiveFilters()" class="filter-field filter-field-clear">
        <label class="filter-label">&nbsp;</label>
        <button
          class="filter-clear"
          title="Limpar filtros"
          @click="clearAll"
        >
          <svg viewBox="0 0 20 20" fill="currentColor" class="clear-icon">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
          <span>Limpar</span>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.filter-panel {
  width: 100%;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: flex-end;
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1 1 90px;
  min-width: 80px;
  max-width: 160px;
}

.filter-label {
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--text-light);
  white-space: nowrap;
  padding-left: 2px;
}

.filter-input {
  width: 100%;
  height: 32px;
  padding: 0.3rem 0.5rem;
  border: 1px solid var(--border-color);
  background-color: var(--surface);
  color: var(--text-dark);
  font-size: 0.8rem;
  border-radius: 6px;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;
}

.filter-input:focus {
  outline: none;
  border-color: var(--focus-border);
  box-shadow: var(--focus-ring);
}

.filter-select {
  cursor: pointer;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 20 20' fill='%236b7280'%3E%3Cpath fill-rule='evenodd' d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z' clip-rule='evenodd'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.45rem center;
  background-size: 18px 18px;
  padding-right: 1.6rem;
}

.filter-clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  width: auto;
  height: 32px;
  padding: 0 0.65rem;
  flex-shrink: 0;
  border: 1px solid var(--danger);
  border-radius: 6px;
  background-color: var(--danger-bg);
  color: var(--danger-text);
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s, transform 0.15s;
  white-space: nowrap;
}

.filter-clear:hover {
  background-color: var(--danger-bg);
  transform: scale(1.08);
}

.filter-field-clear {
  flex: 0 0 auto;
  min-width: unset;
  max-width: unset;
}

.clear-icon {
  width: 13px;
  height: 13px;
  flex-shrink: 0;
}
</style>